import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaUserSlash } from 'react-icons/fa';
import { notify } from '../CustomToast';
import folderService from '../../services/folderService';
import MultiSelectUsers from '../Dashboard/MultiSelectUsers';
import { useI18n } from '../../i18n';
import '../Projects/ProjectComposer.css';
import '../Notes/NotesSection.css';

const FileShareModal = ({ isOpen, onClose, item, onShareUpdated }) => {
    const { t } = useI18n();
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [currentShares, setCurrentShares] = useState([]);

    // For adding new shares
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [newPermission, setNewPermission] = useState('view');
    const [sharing, setSharing] = useState(false);
    const [errors, setErrors] = useState({});

    // For remove confirmation
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [userPendingRemoval, setUserPendingRemoval] = useState(null);
    const [confirmRemoveLoading, setConfirmRemoveLoading] = useState(false);

    const isFolder = item?.type === 'folder';

    // Fetch available users and current shares when modal opens
    useEffect(() => {
        if (!isOpen || !item) return;

        const fetchData = async () => {
            setLoadingUsers(true);
            try {
                const [shareResponse, usersResponse] = await Promise.all([
                    isFolder ? folderService.getFolderShareDetails(item.id) : folderService.getFileShareDetails(item.id),
                    isFolder ? folderService.getAvailableUsersForFolderSharing(item.id) : folderService.getAvailableUsersForFileSharing(item.id)
                ]);

                if (shareResponse?.status) {
                    const shares = isFolder ? shareResponse.data.folder.shared_users : shareResponse.data.file.shared_users;
                    setCurrentShares(shares || []);
                }
                if (usersResponse?.status) {
                    setUsers(usersResponse.data.users || []);
                }
            } catch (err) {
                notify('error', t('pages.file_manager.share_modal.errors.load_details'));
            } finally {
                setLoadingUsers(false);
            }
        };

        fetchData();
        setSelectedUserIds([]);
        setNewPermission('view');
        setErrors({});
    }, [isOpen, item?.id, isFolder]);

    const handleShare = async (e) => {
        if (e) e.preventDefault();
        if (selectedUserIds.length === 0) {
            setErrors({ user_ids: [t('pages.file_manager.share_modal.errors.select_at_least_one')] });
            return;
        }

        setSharing(true);
        try {
            const payload = {
                user_ids: selectedUserIds,
                permission: newPermission
            };

            const response = isFolder
                ? await folderService.shareFolder(item.id, payload)
                : await folderService.shareFile(item.id, payload);

            if (response?.status) {
                const refreshedItem = isFolder ? response.data.folder : response.data.file;
                setCurrentShares(refreshedItem.shared_users || []);

                const usersResponse = isFolder
                    ? await folderService.getAvailableUsersForFolderSharing(item.id)
                    : await folderService.getAvailableUsersForFileSharing(item.id);

                if (usersResponse?.status) {
                    setUsers(usersResponse.data.users || []);
                }
                setSelectedUserIds([]);
                const typeLabel = isFolder ? t('pages.file_manager.folder') : t('pages.file_manager.file');
                notify('success', t('pages.file_manager.share_modal.share_success', { type: typeLabel }));
                onShareUpdated?.();
            }
        } catch (err) {
            const typeLabel = isFolder ? t('pages.file_manager.folder') : t('pages.file_manager.file');
            notify('error', t('pages.file_manager.share_modal.share_error', { type: typeLabel }));
        } finally {
            setSharing(false);
        }
    };

    const openRemoveConfirm = (user) => {
        setUserPendingRemoval(user);
        setConfirmRemoveOpen(true);
        setConfirmRemoveLoading(false);
    };

    const handleConfirmRemove = async () => {
        if (!userPendingRemoval) return;

        setConfirmRemoveLoading(true);
        try {
            const response = isFolder
                ? await folderService.removeFolderShare(item.id, userPendingRemoval.id)
                : await folderService.removeFileShare(item.id, userPendingRemoval.id);

            if (response?.status) {
                const refreshedItem = isFolder ? response.data.folder : response.data.file;
                setCurrentShares(refreshedItem.shared_users || []);

                const usersResponse = isFolder
                    ? await folderService.getAvailableUsersForFolderSharing(item.id)
                    : await folderService.getAvailableUsersForFileSharing(item.id);

                if (usersResponse?.status) {
                    setUsers(usersResponse.data.users || []);
                }
                notify('success', t('pages.file_manager.share_modal.remove_success'));
                onShareUpdated?.();
            }
        } catch (err) {
            notify('error', err.message || t('pages.file_manager.share_modal.remove_error'));
        } finally {
            setConfirmRemoveLoading(false);
            setConfirmRemoveOpen(false);
            setUserPendingRemoval(null);
        }
    };

    const closeRemoveModal = () => {
        setConfirmRemoveOpen(false);
        setUserPendingRemoval(null);
        setConfirmRemoveLoading(false);
    };

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget && !sharing) {
            onClose();
        }
    };

    const handleRemoveBackdropClick = (e) => {
        if (e.target === e.currentTarget && !confirmRemoveLoading) {
            closeRemoveModal();
        }
    };

    const itemName = item?.name || item?.original_filename;

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <>
            <div className="project-composer-overlay" onClick={handleBackdropClick}>
                <div className="project-composer-modal">
                    <div className="project-composer-header">
                        <h2 className="project-composer-title">
                            {t('pages.file_manager.share_modal.title', {
                                type: isFolder ? t('pages.file_manager.folder') : t('pages.file_manager.file'),
                                name: itemName
                            })}
                        </h2>
                        <button
                            className="project-composer-close"
                            onClick={onClose}
                            disabled={sharing}
                            aria-label="Close"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div className="project-composer-body">
                        {/* People with Access */}
                        <div className="ns-section">
                            <h4 className="ns-section-title">
                                {t('pages.file_manager.share_modal.people_with_access', { count: currentShares.length })}
                            </h4>
                            <div className="ns-shares-list">
                                {currentShares.length > 0 ? (
                                    currentShares.map((share) => (
                                        <div key={share.id} className="ns-share-item">
                                            <div className="ns-share-avatar">
                                                {share.avatar ? (
                                                    <img
                                                        src={share.avatar}
                                                        alt={share.name}
                                                        onError={(e) => e.target.classList.add('avatar-hidden')}
                                                    />
                                                ) : (
                                                    <span>{share.name?.charAt(0).toUpperCase()}</span>
                                                )}
                                            </div>
                                            <div className="ns-share-info">
                                                <div className="ns-share-name">
                                                    {share.name}
                                                    <span className={`ns-perm-badge ${share.permission}`}>
                                                        {share.permission === 'edit' ? t('pages.file_manager.share_modal.editor_short') : t('pages.file_manager.share_modal.viewer_short')}
                                                    </span>
                                                </div>
                                                <div className="ns-share-email">{share.email}</div>
                                            </div>
                                            <button
                                                type="button"
                                                className="ns-remove-btn"
                                                onClick={() => openRemoveConfirm(share)}
                                                title={t('pages.file_manager.share_modal.remove_title')}
                                            >
                                                <FaUserSlash />
                                            </button>
                                        </div>
                                    ))
                                ) : (
                                    <div className="ns-empty">
                                        {t('pages.file_manager.share_modal.no_access', {
                                            type: isFolder ? t('pages.file_manager.folder') : t('pages.file_manager.file')
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Add People */}
                        <div className="ns-section">
                            <h4 className="ns-section-title">{t('pages.file_manager.share_modal.add_people')}</h4>
                            <form onSubmit={handleShare} noValidate>
                                <div className="project-form-group">
                                    <label className="project-form-label">
                                        {t('pages.file_manager.share_modal.select_users')} <span className="required">*</span>
                                    </label>
                                    <MultiSelectUsers
                                        users={users}
                                        selectedUserIds={selectedUserIds}
                                        onChange={(selectedIds) => {
                                            setSelectedUserIds(selectedIds);
                                            if (errors.user_ids) {
                                                setErrors({ ...errors, user_ids: null });
                                            }
                                        }}
                                        error={errors.user_ids ? errors.user_ids[0] : null}
                                        loading={loadingUsers}
                                    />
                                </div>

                                <div className="project-form-group">
                                    <label className="project-form-label">{t('pages.file_manager.share_modal.permission')}</label>
                                    <select
                                        className="project-form-select"
                                        value={newPermission}
                                        onChange={(e) => setNewPermission(e.target.value)}
                                    >
                                        <option value="view">{t('pages.file_manager.share_modal.viewer')}</option>
                                        <option value="edit">{t('pages.file_manager.share_modal.editor')}</option>
                                    </select>
                                </div>
                            </form>
                        </div>
                    </div>

                    <div className="project-composer-footer">
                        <button
                            type="button"
                            className="project-composer-btn project-composer-btn-secondary"
                            onClick={onClose}
                            disabled={sharing}
                        >
                            {t('common.actions.cancel')}
                        </button>
                        <button
                            type="button"
                            className="project-composer-btn project-composer-btn-primary"
                            onClick={() => handleShare()}
                            disabled={sharing || selectedUserIds.length === 0}
                        >
                            {sharing ? t('common.status.sending') : t('common.actions.share')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Remove Confirmation */}
            {confirmRemoveOpen && (
                <div className="ns-confirm-overlay" onClick={handleRemoveBackdropClick}>
                    <div className="ns-confirm-modal">
                        <h3 className="ns-confirm-title">{t('pages.file_manager.share_modal.remove_title')}</h3>
                        <p className="ns-confirm-desc">
                            {t('pages.file_manager.share_modal.remove_confirm', {
                                name: userPendingRemoval?.name,
                                type: isFolder ? t('pages.file_manager.folder') : t('pages.file_manager.file')
                            })}
                        </p>
                        <div className="ns-confirm-actions">
                            <button
                                className="project-composer-btn project-composer-btn-secondary"
                                onClick={closeRemoveModal}
                                disabled={confirmRemoveLoading}
                            >
                                {t('common.actions.cancel')}
                            </button>
                            <button
                                className="ns-confirm-remove-btn"
                                onClick={handleConfirmRemove}
                                disabled={confirmRemoveLoading}
                            >
                                {confirmRemoveLoading ? t('common.status.loading') : t('common.actions.remove')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>,
        document.body
    );
};

export default FileShareModal;
